import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, MapPin, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { COMPANY, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGET_OPTIONS = [
  { value: "", label: "Prefer not to say" },
  { value: "under-1000", label: "Under £1,000" },
  { value: "1000-2500", label: "£1,000 – £2,500" },
  { value: "2500-5000", label: "£2,500 – £5,000" },
  { value: "5000-plus", label: "£5,000+" },
  { value: "prefer-to-discuss", label: "Prefer to discuss" },
];

const EMPTY = { name: "", email: "", phone: "", service: "", budget: "", details: "", website: "" };

const Contact = () => {
  useSEO(
    "Contact — SSDE LTD | Let's Start a Conversation",
    "Get in touch with SSDE LTD to discuss your design project. Email company.ssdeltd@gmail.com or call +44 7747 525106. Based in Ilford, United Kingdom."
  );
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    const service = searchParams.get("service");
    if (service) setForm((f) => ({ ...f, service }));
  }, [searchParams]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!form.service) er.service = "Please select a service.";
    if (form.details.trim().length < 10) er.details = "Please tell us a little more about your project.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    setServerError("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service,
        budget: form.budget || null,
        details: form.details.trim(),
        website: form.website,
      });
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(
        typeof detail === "string"
          ? detail
          : "Something went wrong while sending your enquiry. Please try again, or email us directly."
      );
      setStatus("error");
    }
  };

  return (
    <>
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        titleLines={["Let's Start a", "Conversation."]}
        lede="Tell us about your project, your ideas, and the design support you need. Get in touch with SSDE LTD to discuss your requirements."
      />

      <section className="bg-white py-16 sm:py-24" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-charcoal">Contact Details</h2>
              <p className="mt-4 text-sm sm:text-base text-charcoal-mute leading-relaxed max-w-md">
                Every enquiry is read personally. Reach us directly using the
                details below, or send the form and we will respond to you.
              </p>
              <ul className="mt-10 space-y-4">
                <li>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-link"
                    className="group flex items-start gap-4 border border-line bg-offwhite p-5 transition-colors duration-300 hover:border-gold/50"
                  >
                    <span className="inline-flex items-center justify-center w-10 h-10 border border-gold/40 bg-white text-gold-deep shrink-0">
                      <Mail size={17} strokeWidth={1.6} />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">Email</span>
                      <span className="block mt-1 text-sm font-medium text-charcoal group-hover:text-gold-deep transition-colors break-all">
                        {COMPANY.email}
                      </span>
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href={COMPANY.phoneHref}
                    data-testid="contact-phone-link"
                    className="group flex items-start gap-4 border border-line bg-offwhite p-5 transition-colors duration-300 hover:border-gold/50"
                  >
                    <span className="inline-flex items-center justify-center w-10 h-10 border border-gold/40 bg-white text-gold-deep shrink-0">
                      <Phone size={17} strokeWidth={1.6} />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">Phone</span>
                      <span className="block mt-1 text-sm font-medium text-charcoal group-hover:text-gold-deep transition-colors">
                        {COMPANY.phone}
                      </span>
                    </span>
                  </a>
                </li>
                <li className="flex items-start gap-4 border border-line bg-offwhite p-5" data-testid="contact-address-card">
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-gold/40 bg-white text-gold-deep shrink-0">
                    <MapPin size={17} strokeWidth={1.6} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">Address</span>
                    <span className="block mt-1 text-sm font-medium text-charcoal leading-relaxed">
                      {COMPANY.address}
                    </span>
                  </span>
                </li>
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {status === "success" ? (
                <div
                  className="border border-line bg-ivory px-8 py-16 sm:px-14 text-center"
                  data-testid="contact-success-message"
                  role="status"
                >
                  <span className="inline-flex items-center justify-center w-14 h-14 border border-gold/40 bg-white text-gold-deep">
                    <CheckCircle2 size={26} strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-6 font-serif text-2xl sm:text-3xl tracking-tight text-charcoal">
                    Thank you — enquiry received.
                  </h2>
                  <p className="mt-4 max-w-md mx-auto text-sm sm:text-base text-charcoal-mute leading-relaxed">
                    Your message has been sent to our studio. We will review your
                    project details and get back to you shortly.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setForm(EMPTY);
                      setStatus("idle");
                    }}
                    className="btn-outline mt-8"
                    data-testid="contact-send-another-button"
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  data-testid="contact-form"
                  className="border border-line bg-offwhite p-7 sm:p-10"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="cf-name" className="field-label">Full Name *</label>
                      <input
                        id="cf-name"
                        type="text"
                        className="field"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jane Smith"
                        autoComplete="name"
                        aria-invalid={!!errors.name}
                        data-testid="contact-input-name"
                      />
                      {errors.name && <p className="mt-2 text-xs text-red-700" role="alert" data-testid="contact-error-name">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="field-label">Email Address *</label>
                      <input
                        id="cf-email"
                        type="email"
                        className="field"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="jane@company.co.uk"
                        autoComplete="email"
                        aria-invalid={!!errors.email}
                        data-testid="contact-input-email"
                      />
                      {errors.email && <p className="mt-2 text-xs text-red-700" role="alert" data-testid="contact-error-email">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-phone" className="field-label">Phone Number <span className="normal-case tracking-normal text-charcoal/40">(optional)</span></label>
                      <input
                        id="cf-phone"
                        type="tel"
                        className="field"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+44 ..."
                        autoComplete="tel"
                        data-testid="contact-input-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-service" className="field-label">Service Required *</label>
                      <select
                        id="cf-service"
                        className="field"
                        value={form.service}
                        onChange={set("service")}
                        aria-invalid={!!errors.service}
                        data-testid="contact-select-service"
                      >
                        <option value="">Select a service…</option>
                        {SERVICES.map((s) => (
                          <option key={s.slug} value={s.slug}>{s.title}</option>
                        ))}
                        <option value="other">Other / Not sure yet</option>
                      </select>
                      {errors.service && <p className="mt-2 text-xs text-red-700" role="alert" data-testid="contact-error-service">{errors.service}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-budget" className="field-label">Project Budget <span className="normal-case tracking-normal text-charcoal/40">(optional)</span></label>
                      <select
                        id="cf-budget"
                        className="field"
                        value={form.budget}
                        onChange={set("budget")}
                        data-testid="contact-select-budget"
                      >
                        {BUDGET_OPTIONS.map((b) => (
                          <option key={b.value} value={b.value}>{b.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="cf-details" className="field-label">Project Details *</label>
                      <textarea
                        id="cf-details"
                        rows={5}
                        className="field resize-y"
                        value={form.details}
                        onChange={set("details")}
                        placeholder="Tell us about your project, goals and timeline…"
                        aria-invalid={!!errors.details}
                        data-testid="contact-textarea-details"
                      />
                      {errors.details && <p className="mt-2 text-xs text-red-700" role="alert" data-testid="contact-error-details">{errors.details}</p>}
                    </div>
                  </div>

                  <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                    <label htmlFor="cf-website">Website</label>
                    <input
                      id="cf-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </div>

                  {status === "error" && (
                    <div className="mt-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3" role="alert" data-testid="contact-error-banner">
                      <AlertCircle size={17} className="text-red-700 mt-0.5 shrink-0" />
                      <p className="text-sm text-red-800">{serverError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="btn-gold mt-8 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-form-submit-button"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send Enquiry"
                    )}
                  </button>
                  <p className="mt-4 text-xs text-charcoal/50 leading-relaxed">
                    By submitting this form you agree to our handling of your details
                    as described in our Privacy Policy.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
